/**
 * AgentReleasesAdminPage — /admin/agent-releases
 *
 * Founder ask (vibe-coder mode, May 17 2026): drag-and-drop the
 * Tauri installer files (built by GitHub Actions) into VOS — no
 * terminal, no SCP. Admin-only. The /agent-downloads public page
 * then serves whatever is newest per platform.
 *
 * Flow for the founder:
 *   1. "Save to GitHub" in Emergent  →  GH Actions builds 3 installers.
 *   2. Download the zips from the GitHub Releases tab.
 *   3. Drop them onto the 3 zones here. Done.
 *
 * The drop zones are PER-PLATFORM but accept any of the right
 * extensions for that platform. The backend picks the newest file
 * with the right extension and serves it from /api/agent/release/{platform}.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Upload, Monitor, Apple, Cpu, CheckCircle2, AlertTriangle,
  Trash2, RefreshCw, FileArchive, ExternalLink, Github,
} from 'lucide-react';

const PLATFORMS = [
  { key: 'windows', label: 'Windows', icon: Monitor, hint: '.msi  or  setup.exe',
    accept: '.msi,.exe', tint: 'sky',
    note: 'From vos-studio-agent-windows.zip — drag the .msi (preferred) or the setup.exe here.' },
  { key: 'macos',   label: 'macOS',   icon: Apple,   hint: '.dmg  or  .pkg',
    accept: '.dmg,.pkg', tint: 'zinc',
    note: 'From vos-studio-agent-macos.zip — drag the universal .dmg here.' },
  { key: 'linux',   label: 'Linux',   icon: Cpu,     hint: '.deb  or  .rpm  or  .AppImage',
    accept: '.deb,.rpm,.AppImage,.appimage', tint: 'amber',
    note: 'From vos-studio-agent-linux.zip — drag the .deb (Ubuntu/Debian) here. .AppImage works on any distro.' },
];

const TINT = {
  sky:   { ring: 'ring-sky-500/40',   text: 'text-sky-300',   bg: 'bg-sky-500/10',   over: 'border-sky-400 bg-sky-500/15' },
  zinc:  { ring: 'ring-zinc-500/40',  text: 'text-zinc-300',  bg: 'bg-zinc-500/10',  over: 'border-zinc-300 bg-zinc-500/15' },
  amber: { ring: 'ring-amber-500/40', text: 'text-amber-300', bg: 'bg-amber-500/10', over: 'border-amber-400 bg-amber-500/15' },
};

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  let i = 0, u = ['B', 'KB', 'MB', 'GB'];
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i ? 1 : 0)} ${u[i]}`;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function DropZone({ platform, onUpload, progress, lastResult }) {
  const tint = TINT[platform.tint];
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const Icon = platform.icon;
  const busy = progress != null && progress < 100;

  const handleFiles = (files) => {
    if (!files || !files.length) return;
    onUpload(platform.key, files[0]);
  };

  return (
    <div
      data-testid={`drop-zone-${platform.key}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all
                  ${over ? tint.over : 'border-zinc-800 ' + tint.bg}
                  ${tint.ring} ring-1 p-6 flex flex-col gap-3 min-h-[180px]
                  hover:border-zinc-600`}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={platform.accept}
        data-testid={`drop-input-${platform.key}`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${tint.text}`} />
        <h3 className="text-sm font-semibold text-zinc-100">{platform.label}</h3>
        <span className="text-[10px] font-mono text-zinc-500 ml-auto">{platform.hint}</span>
      </div>
      <p className="text-[11px] text-zinc-400">{platform.note}</p>

      <div className="flex-1 flex items-center justify-center">
        {busy ? (
          <div className="w-full">
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progress}%` }}
                data-testid={`upload-progress-${platform.key}`}
              />
            </div>
            <p className="text-[10px] text-emerald-300 mt-2 text-center">Uploading… {progress}%</p>
          </div>
        ) : lastResult ? (
          lastResult.ok ? (
            <div className="text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto" />
              <p className="text-[11px] text-emerald-300 mt-1 font-medium">{lastResult.file}</p>
              <p className="text-[10px] text-zinc-500">Live on /agent-downloads</p>
            </div>
          ) : (
            <div className="text-center">
              <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto" />
              <p className="text-[11px] text-rose-300 mt-1">{lastResult.message}</p>
            </div>
          )
        ) : (
          <div className="text-center">
            <Upload className={`w-6 h-6 ${tint.text} mx-auto opacity-60`} />
            <p className="text-[11px] text-zinc-500 mt-2">Drag installer here or click to browse</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AgentReleasesAdminPage() {
  const { api, user } = useAuth();
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('vos_token');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({});
  const [lastResult, setLastResult] = useState({});
  const [error, setError] = useState('');

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/agent-releases');
      setFiles(res.data?.files || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load releases');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = useCallback(async (platformKey, file) => {
    setProgress((p) => ({ ...p, [platformKey]: 0 }));
    setLastResult((r) => ({ ...r, [platformKey]: null }));

    try {
      const form = new FormData();
      form.append('file', file);

      // XHR for progress events (axios also supports onUploadProgress
      // but XHR keeps the dependency surface tiny)
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiBase}/api/admin/agent-releases/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setProgress((p) => ({ ...p, [platformKey]: pct }));
        }
      };
      xhr.onload = async () => {
        setProgress((p) => ({ ...p, [platformKey]: null }));
        let body = {};
        try { body = JSON.parse(xhr.responseText); } catch { /* ignore */ }
        if (xhr.status >= 200 && xhr.status < 300) {
          setLastResult((r) => ({ ...r, [platformKey]: { ok: true, file: body.file, message: body.message } }));
          fetchFiles();
        } else {
          setLastResult((r) => ({ ...r, [platformKey]: { ok: false, message: body.detail || `Upload failed (${xhr.status})` } }));
        }
      };
      xhr.onerror = () => {
        setProgress((p) => ({ ...p, [platformKey]: null }));
        setLastResult((r) => ({ ...r, [platformKey]: { ok: false, message: 'Network error during upload' } }));
      };
      xhr.send(form);
    } catch (e) {
      setProgress((p) => ({ ...p, [platformKey]: null }));
      setLastResult((r) => ({ ...r, [platformKey]: { ok: false, message: e?.message || 'Upload failed' } }));
    }
  }, [apiBase, token, fetchFiles]);

  const handleDelete = useCallback(async (name) => {
    if (!window.confirm(`Delete ${name}? Artists will lose this download until you upload a replacement.`)) return;
    try {
      await api.post('/admin/agent-releases/delete', { name });
      fetchFiles();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  }, [api, fetchFiles]);

  const grouped = useMemo(() => {
    const out = { windows: [], macos: [], linux: [], unknown: [] };
    files.forEach((f) => { (out[f.platform] || out.unknown).push(f); });
    return out;
  }, [files]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="agent-releases-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">VOS Studio Agent — Releases</h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Upload the installer files built by GitHub Actions. Drop them here and
              they go live on <a href="/agent-downloads" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">vawworld.in/agent-downloads</a> immediately.
            </p>
          </div>
          <button
            onClick={fetchFiles}
            data-testid="refresh-releases-btn"
            className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 border border-zinc-800 hover:bg-zinc-800"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* How to get the installer files (vibe-coder cheatsheet) */}
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 mb-8 text-[13px] mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Github className="w-4 h-4 text-emerald-400" />
            <p className="font-semibold text-zinc-100">Where do these files come from?</p>
          </div>
          <ol className="space-y-1.5 text-zinc-300 ml-1">
            <li><span className="text-emerald-300 font-mono mr-2">1.</span> Click <strong>Save to GitHub</strong> in Emergent's chat input.</li>
            <li><span className="text-emerald-300 font-mono mr-2">2.</span> Open your repo on github.com → <strong>Actions</strong> tab. Wait for the green ✅ (~10 min first time, ~3 min after).</li>
            <li><span className="text-emerald-300 font-mono mr-2">3.</span> Click the green run → scroll to <strong>Artifacts</strong> → download the 3 zips.</li>
            <li><span className="text-emerald-300 font-mono mr-2">4.</span> Unzip each one and drag the installer files onto the matching zone below.</li>
          </ol>
          <p className="text-[11px] text-zinc-500 mt-3">
            Full guide: <span className="font-mono text-emerald-300">/app/agent/HOW_TO_SHIP_THE_AGENT.md</span>
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 px-4 py-2 mb-6 text-[13px]">
            {error}
          </div>
        )}

        {/* Drop zones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {PLATFORMS.map((p) => (
            <DropZone
              key={p.key}
              platform={p}
              onUpload={handleUpload}
              progress={progress[p.key]}
              lastResult={lastResult[p.key]}
            />
          ))}
        </div>

        {/* Live file table */}
        <section className="mt-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
              <FileArchive className="w-4 h-4 text-zinc-400" />
              Currently live ({files.length})
            </h2>
            <a
              href="/agent-downloads"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] text-emerald-400 hover:underline inline-flex items-center gap-1"
              data-testid="open-public-page-link"
            >
              Open public downloads page <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {files.length === 0 ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
              <FileArchive className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No installers uploaded yet.</p>
              <p className="text-[11px] text-zinc-600 mt-1">Drop a .msi / .dmg / .deb above to ship to artists.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-800 overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-900/80 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Platform</th>
                    <th className="text-left px-4 py-2 font-medium">File</th>
                    <th className="text-left px-4 py-2 font-medium">Size</th>
                    <th className="text-left px-4 py-2 font-medium">Uploaded</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.name} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`release-row-${f.name}`}>
                      <td className="px-4 py-2 text-zinc-200 capitalize">{f.platform}</td>
                      <td className="px-4 py-2 font-mono text-zinc-300">{f.name}</td>
                      <td className="px-4 py-2 text-zinc-400">{f.size_human || fmtBytes(f.size_bytes)}</td>
                      <td className="px-4 py-2 text-zinc-500">{fmtAgo(f.uploaded_at)}</td>
                      <td className="px-4 py-2">
                        {f.is_latest ? (
                          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            <CheckCircle2 className="w-3 h-3" /> Live
                          </span>
                        ) : (
                          <span className="text-[11px] text-zinc-500">archived</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleDelete(f.name)}
                          data-testid={`delete-release-${f.name}`}
                          className="text-[12px] text-zinc-500 hover:text-rose-300 inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
